import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Users, FileCheck2, FileWarning, Wallet, Download, Search, Radio, CheckCircle2, XCircle, Receipt } from "lucide-react";
import { toast } from "sonner";

const GNF = (v) => `${Math.round(v || 0).toLocaleString("fr-FR")} GNF`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

const PAY_CHIP = {
  solde: ["Soldé", "bg-green-100 text-green-700"],
  en_cours: ["En cours", "bg-blue-100 text-blue-700"],
  impaye: ["Impayé", "bg-red-100 text-red-700"],
  sans_plan: ["Sans plan", "bg-slate-100 text-slate-500"],
};

const StatCard = ({ icon: Icon, label, value, sub, cls, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
    <div className={`text-2xl font-extrabold tabular-nums ${cls}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

// Panneau latéral : situation financière détaillée de l'élève sélectionné
const StudentSheet = ({ studentId, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!studentId) { setD(null); return; }
    api.get(`/registre/inscrits/${studentId}`)
      .then(({ data }) => setD(data))
      .catch(() => toast.error("Impossible de charger le dossier"));
  }, [studentId]);

  const fin = d?.finance;
  return (
    <Sheet open={!!studentId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="registre-student-sheet">
        {d && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading text-[#1E3A8A] text-xl">
                {d.student.first_name} {d.student.last_name}
              </SheetTitle>
            </SheetHeader>
            <div className="text-sm text-slate-500 mt-0.5">
              {d.student.class_name} · {d.student.matricule} · {d.student.academic_year}
              {d.parent && <> · Parent : <span className="font-semibold text-slate-700">{d.parent.full_name}</span>{d.parent.phone ? ` (${d.parent.phone})` : ""}</>}
            </div>

            {/* Situation financière — gros chiffres */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="rounded-2xl bg-green-50 border border-green-200 p-4" data-testid="sheet-total-paye">
                <div className="text-[11px] uppercase font-bold text-green-700">Total payé</div>
                <div className="text-2xl font-extrabold text-green-700 tabular-nums">{GNF(fin.total_paye_gnf)}</div>
              </div>
              <div className="rounded-2xl bg-red-50 border border-red-200 p-4" data-testid="sheet-reste">
                <div className="text-[11px] uppercase font-bold text-red-700">Reste à payer</div>
                <div className="text-2xl font-extrabold text-red-700 tabular-nums">{GNF(fin.reste_a_payer_gnf)}</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Recouvrement — total dû : {GNF(fin.total_du_gnf)}</span>
                <span className="font-bold">{fin.taux_paiement}%</span>
              </div>
              <Progress value={fin.taux_paiement} className="h-2.5" />
            </div>

            {/* Dossier */}
            <div className="mt-5 rounded-2xl border border-slate-200 p-4">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2">Dossier d&apos;inscription</div>
              {d.dossier.complet ? (
                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm" data-testid="sheet-dossier-complet">
                  <CheckCircle2 className="h-4 w-4" /> Dossier complet
                </div>
              ) : (
                <div className="space-y-1" data-testid="sheet-dossier-incomplet">
                  {d.dossier.manquants.map((m) => (
                    <div key={m} className="flex items-center gap-2 text-red-600 text-sm"><XCircle className="h-4 w-4" /> {m} manquant(e)</div>
                  ))}
                </div>
              )}
            </div>

            {/* Échéancier */}
            {d.echeancier.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase font-bold text-slate-500 mb-2">Échéancier ({d.echeancier.length})</div>
                <div className="rounded-2xl border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto">
                  {d.echeancier.map((e) => (
                    <div key={e.id} className="px-3 py-2 flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium text-slate-700">{e.label}</div>
                        <div className="text-xs text-slate-400">Échéance : {fmtDate(e.due_date)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold tabular-nums">{GNF(e.amount_gnf)}</div>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${e.status === "paid" ? "bg-green-100 text-green-700" : e.status === "partial" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>
                          {e.status === "paid" ? "Payée" : e.status === "partial" ? "Partielle" : "En attente"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historique des paiements */}
            <div className="mt-5">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-1">
                <Receipt className="h-3 w-3" /> Paiements encaissés ({fin.nb_paiements})
              </div>
              <div className="rounded-2xl border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="sheet-paiements">
                {d.paiements.length === 0 && <div className="px-3 py-5 text-center text-slate-400 text-sm">Aucun paiement enregistré.</div>}
                {d.paiements.map((p) => (
                  <div key={p.receipt_number} className="px-3 py-2 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-mono text-xs text-slate-500">{p.receipt_number}</div>
                      <div className="text-xs text-slate-400">{fmtDate(p.paid_at)} · {p.method} · par {p.payer_name || "—"}</div>
                    </div>
                    <div className="font-bold text-green-700 tabular-nums">{GNF(p.amount_gnf)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function RegistreInscritsPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [selected, setSelected] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/registre/inscrits");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement du registre");
    }
  }, []);

  // Temps réel : rafraîchissement automatique toutes les 30 s
  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const classes = useMemo(() => [...new Set((data?.inscrits || []).map((r) => r.class_name))].sort(), [data]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.inscrits || [])
      .filter((r) => !cls || r.class_name === cls)
      .filter((r) => !needle || r.nom_complet.toLowerCase().includes(needle) || r.matricule.toLowerCase().includes(needle))
      .map((r, i) => ({ ...r, numero: i + 1 })); // numérotation incrémentale sur la vue filtrée
  }, [data, q, cls]);

  const exportCsv = () => {
    const head = "N°;Matricule;Nom complet;Classe;Année;Dossier;Total dû;Total payé;Reste à payer;Statut paiement";
    const lines = rows.map((r) =>
      [r.numero, r.matricule, r.nom_complet, r.class_name, r.academic_year,
       r.dossier_complet ? "Complet" : `Incomplet (${r.dossier_manquants.join(", ")})`,
       r.total_du_gnf, r.total_paye_gnf, r.reste_a_payer_gnf, PAY_CHIP[r.statut_paiement][0]].join(";"));
    const blob = new Blob(["\ufeff" + [head, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `registre_inscrits_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const s = data?.stats;
  return (
    <div className="space-y-6" data-testid="registre-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Users className="h-3 w-3" /><span>DIRECTION — REGISTRE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Registre des inscrits</h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            Liste officielle de tous les élèves inscrits, avec situation des dossiers et des paiements.
            <span className="inline-flex items-center gap-1 text-green-600 text-xs font-bold"><Radio className="h-3 w-3 animate-pulse" /> Temps réel</span>
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline" className="gap-1" disabled={!rows.length} data-testid="registre-export-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={Users} label="Total inscrits" value={s.total_inscrits} cls="text-[#1E3A8A]" testid="registre-total" />
          <StatCard icon={FileCheck2} label="Dossiers complets" value={s.dossiers_complets} cls="text-green-700"
            sub={`+ ${s.dossiers_incomplets} incomplets = ${s.dossiers_complets + s.dossiers_incomplets}`} testid="registre-complets" />
          <StatCard icon={FileWarning} label="Dossiers incomplets" value={s.dossiers_incomplets} cls="text-orange-600" testid="registre-incomplets" />
          <StatCard icon={Wallet} label="Encaissé" value={GNF(s.encaisse_gnf)} cls="text-green-700"
            sub={`sur ${GNF(s.attendu_gnf)} attendus`} testid="registre-encaisse" />
          <StatCard icon={Wallet} label="Reste à recouvrer" value={GNF(s.reste_gnf)} cls="text-red-600"
            sub={`Taux : ${s.taux_recouvrement}%`} testid="registre-reste" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom ou matricule…"
            className="pl-9 w-72" data-testid="registre-search" />
        </div>
        <select value={cls} onChange={(e) => setCls(e.target.value)}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="registre-class-filter">
          <option value="">Toutes les classes</option>
          {classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="text-sm text-slate-500 ml-auto">{rows.length} élève(s) affiché(s)</div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-3 text-left w-12">N°</th>
              <th className="px-3 py-3 text-left">Élève</th>
              <th className="px-3 py-3 text-left">Classe</th>
              <th className="px-3 py-3 text-left">Dossier</th>
              <th className="px-3 py-3 text-right">Total dû</th>
              <th className="px-3 py-3 text-right">Payé</th>
              <th className="px-3 py-3 text-right">Reste</th>
              <th className="px-3 py-3 text-left w-36">Recouvrement</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.student_id} onClick={() => setSelected(r.student_id)}
                className="border-t border-slate-100 hover:bg-blue-50/50 cursor-pointer"
                data-testid={`registre-row-${r.matricule}`}>
                <td className="px-3 py-2.5 font-bold text-slate-400 tabular-nums">{r.numero}</td>
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-slate-800">{r.nom_complet}</div>
                  <div className="text-xs text-slate-400 font-mono">{r.matricule}</div>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{r.class_name}</td>
                <td className="px-3 py-2.5">
                  {r.dossier_complet
                    ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Complet</span>
                    : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700" title={r.dossier_manquants.join(", ")}>Incomplet</span>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{GNF(r.total_du_gnf)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold text-green-700">{GNF(r.total_paye_gnf)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold text-red-600">{GNF(r.reste_a_payer_gnf)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Progress value={r.taux_paiement} className="h-1.5 flex-1" />
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${PAY_CHIP[r.statut_paiement][1]}`}>
                      {PAY_CHIP[r.statut_paiement][0]}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Aucun élève trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <StudentSheet studentId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
